import { getApp, getApps, initializeApp, type FirebaseApp } from "firebase/app";
import {
  browserSessionPersistence,
  getAuth,
  getRedirectResult,
  GoogleAuthProvider,
  onIdTokenChanged,
  setPersistence,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  type Auth,
  type User,
} from "firebase/auth";

export interface FirebaseConfiguration {
  readonly apiKey: string;
  readonly authDomain: string;
  readonly projectId: string;
  readonly appId: string;
}

const readConfiguration = (): FirebaseConfiguration | null => {
  const configuration = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
  };
  return Object.values(configuration).every(
    (value) => typeof value === "string" && value.length > 0,
  )
    ? (configuration as FirebaseConfiguration)
    : null;
};

export const firebaseConfiguration = readConfiguration();

const createFirebaseApp = (
  configuration: FirebaseConfiguration,
): FirebaseApp =>
  getApps().length > 0 ? getApp() : initializeApp(configuration);

export const createFirebaseAuth = async (): Promise<Auth | null> => {
  if (!firebaseConfiguration) return null;
  const auth = getAuth(createFirebaseApp(firebaseConfiguration));
  await setPersistence(auth, browserSessionPersistence);
  await getRedirectResult(auth);
  return auth;
};

export const observeIdentity = (
  auth: Auth,
  listener: (user: User | null) => void,
) => onIdTokenChanged(auth, listener);

export const signInWithGoogle = async (auth: Auth): Promise<void> => {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  if (window.matchMedia("(max-width: 760px), (pointer: coarse)").matches) {
    await signInWithRedirect(auth, provider);
    return;
  }
  await signInWithPopup(auth, provider);
};

export const signOutFirebase = (auth: Auth) => signOut(auth);
